import { createContext, useContext } from "react";

/**
 * OneTrust category IDs for common content types
 * These should match your OneTrust configuration
 */
export const ONETRUST_CATEGORIES = {
  /** Essential cookies - always allowed */
  essential: "C0001",
  /** Performance/Analytics cookies */
  performance: "C0002",
  /** Functional cookies */
  functional: "C0003",
  /** Targeting/Advertising cookies */
  targeting: "C0004",
  /** Social media and Embedded content cookies */
  socialMediaAndEmbeds: "C0005",
} as const;

type OneTrustCategories = typeof ONETRUST_CATEGORIES;

export type CookieConsentCategory = keyof OneTrustCategories;
export type CookieConsentCategoryId = OneTrustCategories[CookieConsentCategory];

export type CookieConsentContextType = {
  /** Array of active consent groups */
  activeConsentGroups: string[];
  /** Whether OneTrust script is still loading */
  isOneTrustLoading: boolean;
  /** Allow all categories */
  enableSocialMediaCookies: () => void;
  /** Toggle the OneTrust consent drawer */
  toggleCookiesConsentDrawer: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextType | null>(
  null,
);

/**
 * Return type for useCookieConsent hook.
 */
type UseCookieConsentReturnType = CookieConsentContextType & {
  isConsentAccepted: boolean;
};

export const useCookieConsent = (
  contentType: CookieConsentCategory = "socialMediaAndEmbeds",
): UseCookieConsentReturnType => {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }

  const consentCategory = ONETRUST_CATEGORIES[contentType];

  return {
    ...context,
    // Check if the consent category is accepted
    isConsentAccepted: context.activeConsentGroups.includes(consentCategory),
  };
};

export default CookieConsentContext;
